import { UnsplashPhoto, UnsplashSearchResponse, ProcessedImage, ApiError } from '@/types';
import { config } from '@/config';

class UnsplashService {
  private readonly retryCount = 3;
  private readonly retryDelay = 1000;

  async getImage(): Promise<ProcessedImage> {
    // 尝试多种策略获取图片
    const strategies = [
      () => this.getRandomPhoto(),
      () => this.searchPhotos(),
      () => this.getFallbackImage()
    ];
    
    for (const strategy of strategies) {
      try {
        const result = await strategy();
        if (result) return result;
      } catch (error) {
        console.warn('Unsplash策略失败，尝试下一个:', error);
      }
    }
    
    // 最终降级
    return this.getLocalFallback();
  }

  private async getRandomPhoto(): Promise<ProcessedImage> {
    // 使用主题和集合来获取高质量图片
    const topics = ['nature', 'wallpapers', 'travel', 'architecture'];
    const topic = topics[Math.floor(Math.random() * topics.length)];
    
    const params = new URLSearchParams({
      topics: topic,
      orientation: config.unsplash.orientation,
      content_filter: 'high'
    });
    
    const url = `${config.unsplash.baseUrl}/photos/random?${params}`;
    return await this.fetchWithRetry(url);
  }

  private async searchPhotos(): Promise<ProcessedImage> {
    const query = this.getRandomQuery();
    const params = new URLSearchParams({
      query: query,
      orientation: config.unsplash.orientation,
      per_page: '30',
      content_filter: 'high'
    });
    
    const url = `${config.unsplash.baseUrl}/search/photos?${params}`;
    const response = await this.fetchWithRetry<UnsplashSearchResponse>(url);
    
    if (response && response.results && response.results.length > 0) {
      const photos = response.results;
      const photo = photos[Math.floor(Math.random() * Math.min(photos.length, 10))];
      return this.formatPhotoData(photo);
    }
    
    throw new ApiError('搜索结果为空', 404, 'unsplash');
  }

  private async fetchWithRetry<T = UnsplashPhoto>(url: string, attempt = 1): Promise<T> {
    try {
      const response = await fetch(url, {
        headers: {
          'Authorization': `Client-ID ${config.unsplash.apiKey}`,
          'Accept-Version': 'v1'
        }
      });
      
      // 检查速率限制
      const remaining = response.headers.get('X-Ratelimit-Remaining');
      const limit = response.headers.get('X-Ratelimit-Limit');
      
      if (remaining && limit) {
        console.log(`Unsplash API 速率限制: ${remaining}/${limit}`);
      }
      
      if (response.status === 403 && remaining === '0') {
        throw new ApiError('Unsplash API 速率限制已达上限', 403, 'unsplash');
      }
      
      if (response.status === 500 && attempt < this.retryCount) {
        console.warn(`服务器错误，${this.retryDelay}ms后重试... (尝试 ${attempt}/${this.retryCount})`);
        await this.sleep(this.retryDelay * attempt);
        return await this.fetchWithRetry<T>(url, attempt + 1);
      }
      
      if (!response.ok) {
        throw new ApiError(
          `API请求失败: ${response.status} ${response.statusText}`,
          response.status,
          'unsplash'
        );
      }
      
      const data = await response.json();
      
      // 如果是单张照片响应，直接格式化
      if ((data as UnsplashPhoto).urls) {
        return this.formatPhotoData(data as UnsplashPhoto) as T;
      }
      
      // 如果是搜索响应，返回原始数据
      return data as T;
      
    } catch (error) {
      if (error instanceof ApiError) {
        throw error;
      }
      console.error('Unsplash API请求错误:', error);
      throw new ApiError('网络请求失败', 0, 'unsplash');
    }
  }

  private formatPhotoData(photo: UnsplashPhoto): ProcessedImage {
    // 优先使用 raw（原始尺寸）获得最高质量，fallback 到 full
    let url = photo.urls.raw || photo.urls.full || photo.urls.regular;
    
    // 如果使用 raw，添加质量和尺寸参数以优化加载
    if (photo.urls.raw && url === photo.urls.raw) {
      try {
        const urlObj = new URL(url);
        if (!urlObj.searchParams.has('q')) {
          urlObj.searchParams.set('q', '85'); // 85% 质量，平衡质量和文件大小
        }
        if (!urlObj.searchParams.has('w')) {
          urlObj.searchParams.set('w', '1920'); // 优化后的宽度，适合大多数屏幕
        }
        url = urlObj.toString();
      } catch (error) {
        console.warn('URL处理失败，使用原始URL:', error);
      }
    }
    
    return {
      url,
      photographer: photo.user.name,
      photographer_url: photo.user.links.html,
      photo_url: photo.links.html,
      download_location: photo.links.download_location,
      photo_id: photo.id
    };
  }

  private getFallbackImage(): ProcessedImage {
    // 使用一些流行的 Unsplash 图片作为后备
    const fallbackPhotos = [
      {
        id: 'LJD6U920zVo',
        photographer: 'Jasper Boer',
        photographer_url: 'https://unsplash.com/@jasperboer'
      },
      {
        id: 'ndN00KmbJ1c',
        photographer: 'Matteo Grassi', 
        photographer_url: 'https://unsplash.com/@matteograssi'
      },
      {
        id: '01_igFr7hd4',
        photographer: 'Tim Swaan',
        photographer_url: 'https://unsplash.com/@timswaanphotography'
      }
    ];
    
    const photo = fallbackPhotos[Math.floor(Math.random() * fallbackPhotos.length)];
    
    return {
      url: `https://images.unsplash.com/photo-${photo.id}?w=1920&q=85`,
      photographer: photo.photographer,
      photographer_url: photo.photographer_url,
      photo_url: `https://unsplash.com/photos/${photo.id}`
    };
  }

  private getLocalFallback(): ProcessedImage {
    return {
      url: 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1200 800"%3E%3Cdefs%3E%3ClinearGradient id="a" x1="0%25" y1="0%25" x2="100%25" y2="100%25"%3E%3Cstop offset="0%25" style="stop-color:%234A90E2"%3E%3C/stop%3E%3Cstop offset="100%25" style="stop-color:%237B68EE"%3E%3C/stop%3E%3C/linearGradient%3E%3C/defs%3E%3Crect width="1200" height="800" fill="url(%23a)"/%3E%3C/svg%3E',
      photographer: '本地图片',
      photographer_url: '#',
      photo_url: '#'
    };
  }

  private getRandomQuery(): string {
    const queries = config.unsplash.defaultQueries;
    return queries[Math.floor(Math.random() * queries.length)];
  }

  private sleep(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  // Unsplash 要求：当图片被查看时触发下载事件
  async triggerDownload(download_location: string): Promise<void> {
    if (!download_location) return;
    
    try {
      await fetch(download_location, {
        headers: {
          'Authorization': `Client-ID ${config.unsplash.apiKey}`
        }
      });
    } catch (error) {
      console.warn('触发 Unsplash 下载事件失败:', error);
    }
  }
}

export const unsplashService = new UnsplashService();